import React from 'react';
import { TextField, Autocomplete, InputAdornment } from '@mui/material';
import { FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { SelectOption } from 'global';
import { ConditionsFieldsTypes } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/types';
import { autocompletePropsByType } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/constants';

interface ApplicantFieldProps {
  factKey: string;
  factType: ConditionsFieldsTypes;
  factName: FACT_NAMES_STRING | string;
  value: any;
  onChange: (key: string, value: any) => void;
  stringOptions?: SelectOption[];
}

export const ApplicantField: React.FC<ApplicantFieldProps> = ({
  factKey,
  factType,
  factName,
  value,
  onChange,
  stringOptions,
}) => {
  switch (factType) {
    case ConditionsFieldsTypes.Boolean:
      const booleanOptions: SelectOption[] = [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ];

      const booleanValue = (() => {
        if (value === true || value === 'true') return booleanOptions[0];
        if (value === false || value === 'false') return booleanOptions[1];
        return null;
      })();

      return (
        <Autocomplete
          size="small"
          options={booleanOptions}
          value={booleanValue}
          onChange={(event, newValue) => {
            if (newValue) {
              onChange(factKey, newValue.value === 'true');
            } else {
              onChange(factKey, undefined);
            }
          }}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={params => <TextField {...params} variant="outlined" placeholder="Select..." />}
        />
      );

    case ConditionsFieldsTypes.Number:
      const isCurrency =
        factKey.includes('BALANCE') || factKey.includes('DOWN_PAYMENT') || factKey.includes('DEAL_SIZE');

      return (
        <TextField
          size="small"
          type="number"
          value={value ?? ''}
          onChange={e => {
            const inputValue = e.target.value;
            onChange(factKey, inputValue === '' ? '' : Number(inputValue));
          }}
          variant="outlined"
          fullWidth
          placeholder={isCurrency ? '0.00' : '0'}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9.]*' }}
          InputProps={
            isCurrency
              ? {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }
              : undefined
          }
        />
      );

    case ConditionsFieldsTypes.String:
      const autocompleteConfig = autocompletePropsByType[factName as FACT_NAMES_STRING];
      const optionsToUse = autocompleteConfig?.options || stringOptions || [];

      if (autocompleteConfig || stringOptions) {
        const normalizedValue = (() => {
          if (!value) return null;

          if (typeof value === 'object' && 'value' in value) {
            return value;
          }

          if (typeof value === 'string') {
            const option = optionsToUse.find((opt: SelectOption) => opt.value === value);
            return option || { value, label: value };
          }

          if (Array.isArray(value) && value.length > 0) {
            const firstValue = value[0];
            if (typeof firstValue === 'object' && 'value' in firstValue) {
              return firstValue;
            }
            const option = optionsToUse.find((opt: SelectOption) => opt.value === firstValue);
            return option || { value: firstValue, label: firstValue };
          }

          return null;
        })();

        return (
          <Autocomplete
            size="small"
            options={optionsToUse}
            value={normalizedValue}
            onChange={(event, newValue) => {
              onChange(factKey, newValue);
            }}
            getOptionLabel={option => {
              if (typeof option === 'string') return option;
              return option.label || option.value || '';
            }}
            isOptionEqualToValue={(option, value) => {
              const optionValue = typeof option === 'object' ? option.value : option;
              const valueValue = typeof value === 'object' ? value.value : value;
              return optionValue === valueValue;
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  typeof autocompleteConfig?.textFieldProps?.label === 'string'
                    ? autocompleteConfig.textFieldProps.label
                    : 'Select...'
                }
              />
            )}
          />
        );
      } else {
        return (
          <TextField
            size="small"
            value={Array.isArray(value) ? value.join(', ') : (value ?? '')}
            onChange={e => {
              onChange(factKey, e.target.value);
            }}
            variant="outlined"
            fullWidth
            placeholder="Enter value..."
          />
        );
      }

    default:
      return (
        <TextField
          size="small"
          value={value ?? ''}
          onChange={e => {
            onChange(factKey, e.target.value);
          }}
          variant="outlined"
          fullWidth
          placeholder="Enter value..."
        />
      );
  }
};
